import React, { useState, useEffect } from "react";
import {
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Dialog,
  FormControlLabel,
  ToggleButton,
  Switch,
  ToggleButtonGroup,
  DialogTitle,
  DialogContent,
  DialogContentText,
  Avatar,
  CircularProgress,
  Button,
  Badge,
} from "@mui/material";
import { useHistory } from "react-router-dom";
import {
  Cached as CachedIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  SettingsBrightness as SettingsBrightnessIcon,
  Notifications as NotificationsIcon,
  NotificationsActive as NotificationsActiveIcon,
  ExitToApp as ExitToAppIcon,
  AccountCircle as AccountCircleIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Description as DescriptionIcon,
  CenterFocusStrong as CenterFocusStrongIcon,
  AlignHorizontalLeft as AlignHorizontalLeftIcon,
  Error as ErrorIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import LoginCodeDisplay from "./Login/LoginCodeDisplay";

import { useAtom } from "jotai";
import {
  currentStaffDataAtom,
  isMobileBrowserAtom,
  isElectronAtom,
  showDoublesAtom,
  modalAlignmentAtom,
} from "./-Atoms";
import XeroLogsDialog from "./XeroLogsDialog";
import ErrorLogsDialog from "./ErrorLogsDialog";
import { db, updateDoc, doc, httpsCallable, functions, collection, query, where, onSnapshot } from "./firebaseConfig";

import DockFunctions from "./GlobalFunctions/DockFunctions";

const crypto = require("crypto");

function isPushNotificationSupported() {
  return "serviceWorker" in navigator && "PushManager" in window;
}

async function askUserPermission() {
  return await Notification.requestPermission();
}

function registerServiceWorker() {
  return navigator.serviceWorker.register("/sw.js");
}

function getUserSubscription() {
  return navigator.serviceWorker.ready
    .then(function (serviceWorker) {
      return serviceWorker.pushManager.getSubscription();
    })
    .then(function (pushSubscription) {
      return pushSubscription;
    });
}

function createHash(input) {
  const md5sum = crypto.createHash("md5");
  md5sum.update(Buffer.from(input));
  return md5sum.digest("hex");
}

function UserMenu({
  user,
  so,
  a,
  click,
  close,
  notificationSettingsMenuAnchor,
  handleNotificationSettingsClose,
  handleNotificationSettingsClick,
  userTheme,
  handleThemeChange,
  theme,
  setQrDialogOpen,
  handleSearchModalOpen,
  isMobile,
}) {
  const [currentStaffData] = useAtom(currentStaffDataAtom);
  const [isMobileBrowser] = useAtom(isMobileBrowserAtom);
  const [isElectron] = useAtom(isElectronAtom);
  const [showDoubles, setShowDoubles] = useAtom(showDoublesAtom);
  const [modalAlignment, setModalAlignment] = useAtom(modalAlignmentAtom);
  const history = useHistory();
  const { hideDock } = DockFunctions();

  const [isDarkMode, setIsDarkMode] = useState(true);
  const [notificationTomorrowsJob, setNotificationTomorrowsJob] =
    useState(true);
  const [notificationNextDayChangesMe, setNotificationNextDayChangesMe] =
    useState(true);
  const [
    notificationsNotSupportedDialogOpen,
    setNotificationsNotSupportedDialogOpen,
  ] = useState(false);
  const [notificationRegistered, setNotificationRegistered] = useState(false);
  const [isRegisteringNotifications, setIsRegisteringNotifications] =
    useState(false);
  const [themeSettingsMenuAnchor, setThemeSettingsMenuAnchor] = useState(null);
  const [forceReloadText, setForceReloadText] = useState("Force Reload");
  const [forceReloadNonAdminsText, setForceReloadNonAdminsText] = useState(
    "Force Reload (Non Admins)"
  );
  const [isAlex, setIsAlex] = useState(false);
  const [unreadErrorCount, setUnreadErrorCount] = useState(0);
  const [xeroConnectionError, setXeroConnectionError] = useState(false);
  const [isConnectingXero, setIsConnectingXero] = useState(false);

  const XERO_ADMIN_IDS = [
    "CLJqPv7r8yHYwUv87TpH",
    "4zRbDoY2K2C8ZRKixaGs",
    "8jXVYoCymxHLQngklbWE",
  ];
  const isXeroAdmin = XERO_ADMIN_IDS.includes(currentStaffData?.id);

  const savePushNotificationSubscriptionToUserData = httpsCallable(functions, "savePushNotificationSubscriptionToUserData");
  const forceReload = httpsCallable(functions, "forceReload");
  const forceReloadNonAdmins = httpsCallable(functions, "forceReloadNonAdmins");

  const iconStyle = {
    marginRight: "20px",
    color: theme === "dark" ? "#ffffff" : "#000000",
  };

  const pStyle = {
    paddingRight: "20px",
    color: theme === "dark" ? "#ffffff" : "#000000",
  };

  const menuPaperStyle = {
    background: theme === "dark" ? "#1f1f1f" : "#ebebeb",
  };

  useEffect(() => {
    const darkModeMediaQuery = window.matchMedia(
      "(prefers-color-scheme: dark)"
    );

    const handleDarkModeChange = (e) => setIsDarkMode(e.matches);
    darkModeMediaQuery.addEventListener("change", handleDarkModeChange);

    return () => {
      darkModeMediaQuery.removeEventListener("change", handleDarkModeChange);
    };
  }, []);

  useEffect(() => {
    setIsAlex(currentStaffData.id === "8jXVYoCymxHLQngklbWE");
  }, [currentStaffData]);

  useEffect(() => {
    if (!isAlex) {
      setUnreadErrorCount(0);
      return;
    }

    const q = query(
      collection(db, "ErrorLogs"),
      where("errorRead", "==", false)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setUnreadErrorCount(snapshot.size);
    }, (error) => {
      console.error("Error listening to unread error count:", error);
    });

    return () => unsubscribe();
  }, [isAlex]);

  useEffect(() => {
    if (!isXeroAdmin) {
      setXeroConnectionError(false);
      return;
    }

    const unsubscribe = onSnapshot(
      doc(db, "xero-details", "connection-status"),
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          setXeroConnectionError(data.connected === false);
        } else {
          setXeroConnectionError(false);
        }
      },
      (error) => {
        console.error("Error listening to Xero connection status:", error);
      }
    );

    return () => unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isXeroAdmin]);

  useEffect(() => {
    const checkRegistered = async () => {
      if (!currentStaffData?.notificationSubscriptions) return;
      if (!isPushNotificationSupported()) return;
      if (!("serviceWorker" in navigator)) return;

      const serviceWorkers = await navigator.serviceWorker.getRegistrations();
      if (serviceWorkers.length === 0) return;

      const subscription = await getUserSubscription();
      const subscriptionId = createHash(JSON.stringify(subscription));

      const isRegistered = currentStaffData.notificationSubscriptions.some(
        (sub) => sub.id === subscriptionId
      );

      setNotificationRegistered(isRegistered);
    };

    checkRegistered();
  }, [currentStaffData]);

  const handleThemeSettingsClose = () => {
    setThemeSettingsMenuAnchor(null);
  };

  const handleNotificationTomorrowsJobToggle = (newValue) => {
    setNotificationTomorrowsJob(newValue);
    try {
      updateDoc(doc(db, "StaffData", user?.staffData?.id), { 
        notificationTomorrowsJob: newValue 
      });
    } catch (e) {
      console.log("Error updating notification setting");
    }
  };

  const handleNotificationNextDayChangesMeToggle = (newValue) => {
    setNotificationNextDayChangesMe(newValue);
    updateDoc(doc(db, "StaffData", user?.staffData?.id), { 
      notificationNextDayChangesMe: newValue 
    });
  };

  const handleShowDoublesToggle = (newValue) => {
    setShowDoubles(!newValue);
    updateDoc(doc(db, "StaffData", user.staffData.id), { 
      showDoubles: !newValue 
    });
  };

  const handleModalAlignmentToggle = (event, newValue) => {
    if (newValue !== null) {
      setModalAlignment(newValue);
      updateDoc(doc(db, "StaffData", user.staffData.id), { 
        modalAlignment: newValue 
      });
    }
  };

  const handleForceReload = async () => {
    setForceReloadText("Reloading Instances...");
    await forceReload();
    setForceReloadText("Force Reload");
    close();
  };

  const handleForceReloadNonAdmins = async () => {
    setForceReloadNonAdminsText("Reloading Instances...");
    await forceReloadNonAdmins();
    setForceReloadNonAdminsText("Force Reload (Non Admins)");
    close();
  };

  const navigateToProfile = () => {
    history.push("/staff");
  };

  const setupNotifications = async () => {
    if (!isPushNotificationSupported()) {
      setNotificationsNotSupportedDialogOpen(true);
      return;
    }

    setIsRegisteringNotifications(true);
    const permission = await askUserPermission();

    if (permission !== "granted") {
      setIsRegisteringNotifications(false);
      return;
    }

    registerServiceWorker();
    const serviceWorker = await navigator.serviceWorker.ready;
    const subscriptionRaw = await serviceWorker.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey:
        "BKluKzkYf9wTIVmdWJMy9szdvr-utCUC5XqlAZgLvcTrqDAuGYynQMFZaHYFLozzyxukkGZ6-FxjGYvUxPv2x7w",
    });

    const subscriptionString = JSON.stringify(subscriptionRaw);
    const subscription = JSON.parse(subscriptionString);

    await savePushNotificationSubscriptionToUserData({
      subscription: subscription,
    });

    setIsRegisteringNotifications(false);
  };

  // eslint-disable-next-line no-unused-vars
  const handleQrDialogOpen = () => {
    setQrDialogOpen(true);
    hideDock();
  };

  const [xeroLogsDialogOpen, setXeroLogsDialogOpen] = useState(false);
  const [errorLogsDialogOpen, setErrorLogsDialogOpen] = useState(false);
  
  const handleXeroLogsClick = () => {
    setXeroLogsDialogOpen(true);
    close();
  };

  const handleConnectXero = async () => {
    setIsConnectingXero(true);
    try {
      const getXeroAuthUrl = httpsCallable(functions, "getXeroAuthUrl");
      const result = await getXeroAuthUrl();
      if (result.data?.authUrl) {
        window.open(result.data.authUrl, "_blank", "noopener,noreferrer");
      }
    } catch (error) {
      console.error("Error getting Xero auth URL:", error);
      alert("Failed to initiate Xero connection. Please try again.");
    } finally {
      setIsConnectingXero(false);
      close();
    }
  };

  const handleErrorLogsClick = () => {
    setErrorLogsDialogOpen(true);
    close();
  };


  const menuSx = {
    "& .MuiPaper-root": {
      backgroundColor: isDarkMode ? "#f0f0f0" : "#f0f0f0",
      color: isDarkMode ? "#1f1f1f" : "#1f1f1f",
      border: "2px solid rgba(0, 0, 0, 0.1)",
    },
  };

  const renderThemeToggleButtons = () => (
    <ToggleButtonGroup
      color="primary"
      value={userTheme || "dark"}
      exclusive
      onChange={(event, newValue) => handleThemeChange(newValue)}
      sx={{
        display: "flex",
        justifyContent: "center",
        width: "100%",
        marginBottom: 1,
      }}
    >
      <ToggleButton
        value="light"
        sx={{ height: 40 }}
        onClick={handleThemeSettingsClose}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <LightModeIcon style={{ marginRight: 8 }} fontSize="small" />
          <span style={{ fontSize: 10 }}>Light</span>
        </div>
      </ToggleButton>
      <ToggleButton
        value="automatic"
        sx={{ height: 40 }}
        onClick={handleThemeSettingsClose}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <SettingsBrightnessIcon style={{ marginRight: 8 }} fontSize="small" />
          <span style={{ fontSize: 10 }}>Auto</span>
        </div>
      </ToggleButton>
      <ToggleButton
        value="dark"
        sx={{ height: 40 }}
        onClick={handleThemeSettingsClose}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <DarkModeIcon style={{ marginRight: 8 }} fontSize="small" />
          <span style={{ fontSize: 10 }}>Dark</span>
        </div>
      </ToggleButton>
    </ToggleButtonGroup>
  );

  return (
    <div
      style={{ display: "flex", alignItems: "center", color: "red !important" }}
    >
      {isMobileBrowser && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <LoginCodeDisplay
            currentStaffData={currentStaffData}
            theme={theme}
          />
        </div>
      )}

      {/* Mobile Search Icon - to the right of LoginCodeDisplay */}
      {isMobile && (
        <div
          style={{
            marginRight: 1,
          }}
        >
          <IconButton
            onClick={handleSearchModalOpen}
            size="medium"
            sx={{
              color: theme === "dark" ? "#ffffff" : "#4d4d4d",
              backgroundColor: "transparent", // Remove background circle
              "&:hover": {
                backgroundColor: theme === "dark"
                  ? "rgba(255, 255, 255, 0.1)"
                  : "rgba(0, 0, 0, 0.1)",
              },
              transition: "all 0.2s ease-in-out",
            }}
          >
            <SearchIcon />
          </IconButton>
        </div>
      )}

      {!isElectron && (
        <IconButton
          onClick={
            notificationRegistered
              ? handleNotificationSettingsClick
              : setupNotifications
          }
        >
          {notificationRegistered ? (
            <NotificationsActiveIcon
              sx={{ color: theme === "dark" ? "#ffffff" : "#4d4d4d" }}
            />
          ) : isRegisteringNotifications ? (
            <CircularProgress size={32} />
          ) : (
            <NotificationsIcon
              sx={{ color: theme === "dark" ? "#ffffff" : "#4d4d4d" }}
            />
          )}
        </IconButton>
      )}

      {isElectron && (
        <Button variant="contained" size="small" onClick={so}>
          Logout
        </Button>
      )}

      <IconButton onClick={click} style={{ marginRight: -16 }}>
        {isAlex && unreadErrorCount > 0 ? (
          <Badge badgeContent={unreadErrorCount} color="error">
            <Avatar alt={user.displayName} src={user.photoURL} />
          </Badge>
        ) : (
          <Avatar alt={user.displayName} src={user.photoURL} />
        )}
      </IconButton>

      <Dialog
        onClose={() => setNotificationsNotSupportedDialogOpen(false)}
        open={notificationsNotSupportedDialogOpen}
      >
        <DialogTitle>Notifications Not Supported 🫠</DialogTitle>
        <DialogContent>
          <DialogContentText>
            If you are on an iPhone, ensure you are on iOS 16.4 or later & also
            have the app installed to your homescreen, it wont work in a
            browser.
            <br />
            <br />
            If you are on an Android phone, good luck you are on your own 🤷🏼‍♂️
          </DialogContentText>
        </DialogContent>
      </Dialog>

      <Menu
        id="theme-settings-menu"
        anchorEl={themeSettingsMenuAnchor}
        keepMounted
        open={Boolean(themeSettingsMenuAnchor)}
        onClose={handleThemeSettingsClose}
        disableScrollLock={true}
        sx={menuSx}
        PaperProps={{
          style: {
            width: 400,
            ...menuPaperStyle,
          },
        }}
      >
        <div>
          <ToggleButtonGroup
            color="primary"
            value={userTheme || "dark"}
            exclusive
            onChange={(event, newValue) => handleThemeChange(newValue)}
            sx={{
              display: "flex",
              justifyContent: "center",
              marginRight: 2,
              marginLeft: 2,
            }}
          >
            <ToggleButton
              value="light"
              sx={{ width: 132, height: 40 }}
              onClick={handleThemeSettingsClose}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <LightModeIcon style={{ marginRight: 8 }} fontSize="small" />
                <span style={{ fontSize: 10 }}>Light</span>
              </div>
            </ToggleButton>
            <ToggleButton
              value="automatic"
              sx={{ width: 132, height: 40 }}
              onClick={handleThemeSettingsClose}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <SettingsBrightnessIcon
                  style={{ marginRight: 8 }}
                  fontSize="small"
                />
                <span style={{ fontSize: 10 }}>Auto</span>
              </div>
            </ToggleButton>
            <ToggleButton
              value="dark"
              sx={{ width: 132, height: 40 }}
              onClick={handleThemeSettingsClose}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <DarkModeIcon style={{ marginRight: 8 }} fontSize="small" />
                <span style={{ fontSize: 10 }}>Dark</span>
              </div>
            </ToggleButton>
          </ToggleButtonGroup>
        </div>
      </Menu>

      <Menu
        id="notification-settings-menu"
        anchorEl={notificationSettingsMenuAnchor}
        keepMounted
        open={Boolean(notificationSettingsMenuAnchor)}
        onClose={handleNotificationSettingsClose}
        disableScrollLock={true}
        sx={menuSx}
        PaperProps={{
          style: {
            width: 280,
            ...menuPaperStyle,
          },
        }}
      >
        <p
          style={{
            fontWeight: 800,
            textAlign: "center",
            color: theme === "dark" ? "#ffffff" : "#000000",
          }}
        >
          Notification Settings
        </p>
        <MenuItem
          onClick={() =>
            handleNotificationTomorrowsJobToggle(!notificationTomorrowsJob)
          }
        >
          <FormControlLabel
            onClick={(event) => {
              event.stopPropagation();
            }}
            control={
              <Switch
                checked={notificationTomorrowsJob}
                onChange={(e) =>
                  handleNotificationTomorrowsJobToggle(e.target.checked)
                }
              />
            }
            sx={{ color: theme === "dark" ? "#ffffff" : "#000000" }}
            label="Tomorrow's Job"
          />
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() =>
            handleNotificationNextDayChangesMeToggle(
              !notificationNextDayChangesMe
            )
          }
        >
          <FormControlLabel
            onClick={(event) => {
              event.stopPropagation();
            }}
            control={
              <Switch
                checked={notificationNextDayChangesMe}
                onChange={(e) =>
                  handleNotificationNextDayChangesMeToggle(e.target.checked)
                }
              />
            }
            sx={{ color: theme === "dark" ? "#ffffff" : "#000000" }}
            label="Next Day Changes (Me)"
          />
        </MenuItem>
      </Menu>

      <Menu
        id="user-menu"
        anchorEl={a}
        keepMounted
        open={Boolean(a)}
        onClose={close}
        disableScrollLock={true}
        sx={menuSx}
        PaperProps={{
          style: menuPaperStyle,
        }}
      >
        <MenuItem disabled>
          <p style={pStyle}>{user.displayName}</p>
        </MenuItem>
        <MenuItem disabled>
          <p style={pStyle}>{user.email}</p>
        </MenuItem>

        {!isElectron && (
          <>
            <Divider />
            <MenuItem onClick={navigateToProfile}>
              <AccountCircleIcon sx={iconStyle} />
              <p style={pStyle}>My Info</p>
            </MenuItem>
          </>
        )}

        <Divider />

        {renderThemeToggleButtons()}

        <Divider />

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <ToggleButton
            value={showDoubles || false}
            selected={showDoubles}
            sx={{
              height: 40,
              width: 243,
              marginBottom: 1,
              marginTop: 1,
            }}
            onChange={(e, newValue) => handleShowDoublesToggle(newValue)}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              {showDoubles ? (
                <div>
                  <VisibilityIcon style={{ marginRight: 8 }} fontSize="small" />
                  <span style={{ fontSize: 10 }}>Hide Doubled Staff</span>
                </div>
              ) : (
                <div>
                  <VisibilityOffIcon
                    style={{ marginRight: 8 }}
                    fontSize="small"
                  />
                  <span style={{ fontSize: 10 }}>Show Doubled Staff</span>
                </div>
              )}
            </div>
          </ToggleButton>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <ToggleButtonGroup
            color="primary"
            value={modalAlignment || "center"}
            exclusive
            onChange={handleModalAlignmentToggle}
            sx={{
              height: 40,
              width: 243,
              marginBottom: 1,
              marginTop: 1,
            }}
          >
            <ToggleButton value="left" sx={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <AlignHorizontalLeftIcon
                  style={{ marginRight: 4 }}
                  fontSize="small"
                />
                <span style={{ fontSize: 10 }}>Left</span>
              </div>
            </ToggleButton>
            <ToggleButton value="center" sx={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <CenterFocusStrongIcon
                  style={{ marginRight: 4 }}
                  fontSize="small"
                />
                <span style={{ fontSize: 10 }}>Center</span>
              </div>
            </ToggleButton>
          </ToggleButtonGroup>
        </div>

        {isXeroAdmin && xeroConnectionError && (
          <>
            <Divider />
            <MenuItem
              onClick={handleConnectXero}
              disabled={isConnectingXero}
              sx={{
                marginTop: 1,
                backgroundColor: "rgba(220, 38, 38, 0.08)",
                "&:hover": {
                  backgroundColor: "rgba(220, 38, 38, 0.15)",
                },
              }}
            >
              {isConnectingXero ? (
                <CircularProgress
                  size={24}
                  sx={{ marginRight: "20px", color: "#dc2626" }}
                />
              ) : (
                <ErrorIcon sx={{ ...iconStyle, color: "#dc2626" }} />
              )}
              <p style={{ ...pStyle, color: "#dc2626", fontWeight: 600 }}>
                Connect Xero
              </p>
            </MenuItem>
          </>
        )}

        {isAlex && (
          <>
            <Divider />
            <MenuItem onClick={handleErrorLogsClick} sx={{ marginTop: 1 }}>
              <ErrorIcon sx={iconStyle} />
              <p style={pStyle}>Error Logs</p>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleXeroLogsClick} sx={{ marginTop: 1 }}>
              <DescriptionIcon sx={iconStyle} />
              <p style={pStyle}>Xero Logs</p>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleForceReload} sx={{ marginTop: 1 }}>
              <CachedIcon sx={iconStyle} />
              <p style={pStyle}>{forceReloadText}</p>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleForceReloadNonAdmins}>
              <CachedIcon sx={iconStyle} />
              <p style={pStyle}>{forceReloadNonAdminsText}</p>
            </MenuItem>
          </>
        )}

        <Divider />

        <MenuItem onClick={so}>
          <ExitToAppIcon
            style={{
              paddingRight: "10px",
              color: theme === "dark" ? "#ffffff" : "#000000",
            }}
          />
          <span
            style={{
              color: theme === "dark" ? "#ffffff" : "#000000",
            }}
          >
            Logout
          </span>
        </MenuItem>
      </Menu>
      <XeroLogsDialog
        open={xeroLogsDialogOpen}
        onClose={() => setXeroLogsDialogOpen(false)}
      />
      <ErrorLogsDialog
        open={errorLogsDialogOpen}
        onClose={() => setErrorLogsDialogOpen(false)}
      />
    </div>
  );
}

export default UserMenu;
